import { ecId, HandBack, record, Refusal, upper, type Row } from '../janus-engine/ability/context.js';
import { bookReadingFacts, planBookActivity, READING_STATUS } from '../janus-engine/actions/book-activity.js';
import { inventoryIdOf, templateIdOf } from '../janus-engine/minigames/rows.js';
import { cachedInventoryRow } from '../janus-engine/inventory/cached-inventory-trees.js';
import { inventoryField } from '../shared/array-types.js';
import { normalizePhpListish } from '../shared/php-listish.js';
import type { ReadingFacts } from '../janus-engine/ability/timed-finish/reading.js';
import { ensureActorRowHeld, runRowOnly } from '../shared/actor-row-loader.js';
import { activityContext, activityError, decideActivity, ensureActivityMembers, loadActivityStatus, recoverActivity, saveActivity } from './activity-truth.js';
import { actorEncounterFor, actorRowFor } from './movement-truth.js';
import { fetchAndCacheObject } from './object-cache.js';
import { payloadDryRunRollSource } from '../janus-engine/ability/environmental-payload.js';

type BookHost = { id: string; row: Row };
const bookOwners = new Map<string, string>();
const missingBooks = new Map<string, { witness: string; read: Promise<Row | null> }>();
const bookDefinitions = new Map<string, { witness: string; read: Promise<Row | null> }>();

/** Physical trees only. A detached object-cache copy cannot resurrect a removed/moved instance. */
function* bookHosts(encounterId: string, actor: Row): Generator<BookHost> {
  actor = record(actorRowFor(encounterId, ecId(actor))) ?? actor;
  yield { id: ecId(actor), row: actor };
  const encounter = record(actorEncounterFor(encounterId));
  for (const raw of normalizePhpListish(encounter?.encounter_creatures_arr)) {
    const member = record(raw), id = member && ecId(member);
    if (id && id !== ecId(actor)) yield { id, row: record(actorRowFor(encounterId, id)) ?? member! };
  }
  if (encounter) yield { id: encounterId, row: encounter };
}
function bookRevision(host: BookHost, encounterId: string): number {
  const encounter = record(actorEncounterFor(encounterId));
  const base = record(record(window.ftReadHeldEntityBase?.(encounterId, 'encounter') ?? encounter?._entity_base)?.base);
  // Mounted envelopes can lag saved delivery. Read the runtime's member BASE coverage,
  // independently of the enclosing encounter revision and the creature's movement counter.
  const member = normalizePhpListish(base?.encounter_creatures_arr).map(record).find(row => upper(row?.entity_id) === host.id);
  return Math.max(0, window.ftBaseAppliedRevision?.(host.id) ?? 0, ...[host.row._entity_base, host.row._inventory_held, host.row._inventory_base,
    window.ftReadHeldInventoryBase?.(host.id), window.ftReadHeldEntityBase?.(host.id, 'creature'),
    window.ftReadHeldEntityBase?.(host.id, 'pc'), member].flatMap(value => {
      const held = record(value);
      return held && upper(held.entity_id) === host.id ? [Number(held.base_rev ?? 0)] : [];
    }));
}
function bookDefinition(id: string, ctx: ReturnType<typeof activityContext>): Promise<Row | null> {
  const store = (globalThis as { __ftDefinitionStore?: { epoch?: number | null; cacheGeneration?: number } }).__ftDefinitionStore;
  const witness = `${store?.epoch}:${store?.cacheGeneration}`, previous = bookDefinitions.get(id);
  if (previous?.witness === witness) return previous.read;
  const candidate = !previous ? record(ctx.read.definition(id)) : null;
  const held = candidate && (record(candidate.item_layer_types) || id === READING_STATUS && record(candidate.computed)) ? candidate : null;
  const read = held ? Promise.resolve(held) : fetchAndCacheObject(id).then(record);
  bookDefinitions.set(id, { witness, read });
  return read;
}

async function heldBook(inventoryId: string, encounterId: string, actor: Row): Promise<Row> {
  const id = upper(inventoryId);
  const locate = () => {
    let fallback: BookHost = { id: ecId(actor), row: actor };
    for (const host of bookHosts(encounterId, actor)) {
      if (host.id === bookOwners.get(id)) fallback = host;
      const row = cachedInventoryRow(host.id === encounterId ? { encounter_inventories_arr: host.row.encounter_inventories_arr } : host.row, id);
      if (row) return { host, row };
    }
    return { host: fallback, row: null };
  };
  let found = locate(), required = window.ftBaseHeardRevision?.(found.host.id) ?? 0;
  if (bookRevision(found.host, encounterId) < required) {
    await window.ftAwaitEntityBase?.([found.host.id]);
    found = locate(); required = window.ftBaseHeardRevision?.(found.host.id) ?? 0;
  }
  const current = bookRevision(found.host, encounterId) >= required;
  let book = current ? found.row : null;
  if (book) {
    bookOwners.set(id, found.host.id); missingBooks.delete(id);
  } else if (current && bookOwners.has(id)) {
    throw new HandBack('book-reading:book-not-held');
  } else {
    // No mutable TTL: only this missing/stale holder revision permits one exact read.
    const witness = `${found.host.id}:${required}:${bookRevision(found.host, encounterId)}`;
    let pending = missingBooks.get(id);
    if (pending?.witness !== witness) {
      pending = { witness, read: fetchAndCacheObject(id, { fresh: !current }).then(record) };
      missingBooks.set(id, pending);
    }
    book = await pending.read;
    // The canonical row may have arrived/moved while the SELECT was in flight.
    const latest = locate(), newest = window.ftBaseHeardRevision?.(latest.host.id) ?? 0;
    if (bookRevision(latest.host, encounterId) >= newest) {
      if (latest.row) { book = latest.row; bookOwners.set(id, latest.host.id); missingBooks.delete(id); }
      else if (bookOwners.has(id)) throw new HandBack('book-reading:book-not-held');
    } else if (newest > required) throw new HandBack('book-reading:book-not-held');
  }
  if (!book || Number(inventoryField(book, 'inventory_deleted') ?? 0)) throw new HandBack('book-reading:book-not-held');
  const ctx = activityContext(encounterId, ecId(actor), actor);
  const definition = await bookDefinition(templateIdOf(book), ctx);
  await bookDefinition(READING_STATUS, ctx);
  // Definitions remain shared; only the single lean instance gets its identity/layers view.
  return definition ? { ...book, items: definition.items, item_layer_types: definition.item_layer_types } : book;
}

async function history(encounterId: string, actorId: string, inventoryId: string): Promise<{ rows: Row[]; ownerId: string; canDelete: boolean }> {
  const response = await fetch('/options/api/activity_state_inc.php', { method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ schema: 'janus.activity.truth.v1', operation_id: crypto.randomUUID().toUpperCase(), actor_id: actorId, encounter_id: encounterId,
      expected_rev: 0, inventory_id: inventoryId, kind: 'book' }), signal: AbortSignal.timeout(15000) });
  const body = record(await response.json());
  if (!response.ok || body?.ok !== true || !Array.isArray(body.history)) throw new HandBack('book-reading:history-not-held');
  return { rows: body.history.map(record).filter((r): r is Row => !!r), ownerId: upper(body.owner_id), canDelete: body.can_delete_rolls === true };
}

export async function loadReadingFacts(encounterId: string, actor: Row, book: Row): Promise<ReadingFacts> {
  const snapshot = await history(encounterId, ecId(actor), inventoryIdOf(book));
  return bookReadingFacts(activityContext(encounterId, ecId(actor), actor), actor, book, snapshot.rows);
}

export async function commitBookActivity(input: { action: 'read' | 'unread'; inventoryId: string; actorId: string; encounterId?: string }): Promise<{ ok: boolean; error?: string }> {
  return runRowOnly(input.actorId, async () => {
    try {
      const where = await ensureActorRowHeld(input.actorId, input.encounterId, { what: 'Reading' });
      if (where.kind !== 'encounter') throw new HandBack('book-reading:actor-not-in-encounter');
      await recoverActivity(where.encounterId, input.actorId);
      await ensureActivityMembers(where.encounterId, [input.actorId]);
      const actor = record(actorRowFor(where.encounterId, input.actorId));
      if (!actor) throw new HandBack('book-reading:book-or-actor-not-held');
      const book = await heldBook(input.inventoryId, where.encounterId, actor);
      const ctx = activityContext(where.encounterId, input.actorId, actor);
      const snapshot = await history(where.encounterId, input.actorId, input.inventoryId);
      if (input.action === 'unread' && !snapshot.canDelete) throw new Refusal('roll-delete:gm-required');
      const prior = input.action === 'read' ? await loadActivityStatus(where.encounterId, input.actorId, READING_STATUS) : null;
      const plan = decideActivity(ctx, fresh => planBookActivity(fresh, actor, book, snapshot.rows, input.action, snapshot.ownerId, prior));
      plan.rollPrivate = Number(getSidelogContext()?.roll_private ?? 0);
      await saveActivity(where.encounterId, input.actorId, 'book_' + input.action, plan);
      if (input.action === 'unread') window.dispatchEvent(new CustomEvent('ft:book-unread', { detail: { inventoryId: input.inventoryId } }));
      return { ok: true };
    } catch (error) { return activityError(error); }
  });
}
Object.assign(window, { ftCommitBookActivity: commitBookActivity, ftBookActivity: (action: 'read' | 'unread', inventoryId: string) => {
  const context = getSidelogContext();
  return commitBookActivity({ action, inventoryId, actorId: String(window.ftGetActiveCasterId?.() ?? context?.caster_id ?? ''), encounterId: String(context?.encounter_id ?? '') });
} });

/** Cards use the same browser verdict as the click; a missing read record never exposes Unread. */
const controlEpochs = new WeakMap<HTMLElement, number>();
async function refreshBookControls(root: HTMLElement): Promise<void> {
  const epoch = (controlEpochs.get(root) ?? 0) + 1; controlEpochs.set(root, epoch);
  const read = root.querySelector<HTMLButtonElement>('[data-ft-book-read]'), reason = root.querySelector<HTMLElement>('[data-ft-book-read-reason]');
  const unread = root.querySelector<HTMLButtonElement>('[data-ft-book-unread]');
  if (read) read.disabled = true;
  if (unread) unread.hidden = true;
  if (reason) reason.title = 'Checking reading requirements…';
  let message = '', hasRecord = false;
  try {
    const context = getSidelogContext(), actorId = upper(window.ftGetActiveCasterId?.() ?? context?.caster_id), encounterId = upper(context?.encounter_id);
    const actor = record(actorRowFor(encounterId, actorId));
    if (!actor) throw new Refusal('Select a character to read this book.');
    const book = await heldBook(root.dataset.ftBookActions ?? '', encounterId, actor), snapshot = await history(encounterId, actorId, inventoryIdOf(book));
    const ctx = activityContext(encounterId, actorId, actor);
    // Availability uses only dry dice, including the final branch of decideActivity.
    const preview = { ...ctx, roll: payloadDryRunRollSource('max') };
    if (snapshot.canDelete) {
      try { decideActivity(preview, fresh => planBookActivity(fresh, actor, book, snapshot.rows, 'unread', snapshot.ownerId)); hasRecord = true; } catch { /* No matching owner/instance record. */ }
    }
    decideActivity(preview, fresh => planBookActivity(fresh, actor, book, snapshot.rows, 'read', snapshot.ownerId));
  } catch (error) { message = error instanceof Refusal ? error.refusal : error instanceof HandBack ? error.reason : 'Reading requirements could not be loaded.'; }
  if (controlEpochs.get(root) !== epoch || !root.isConnected) return;
  if (read) { read.disabled = !!message; read.title = message; }
  if (reason) reason.title = message;
  if (unread) unread.hidden = !hasRecord;
}
const pendingControls = new Set<HTMLElement>();
const activeControls = new WeakSet<HTMLElement>();
let controlTimer: ReturnType<typeof setTimeout> | undefined;
function queueBookControls(root: HTMLElement): void {
  // Invalidate an in-flight preview immediately; a burst refreshes each connected card once.
  controlEpochs.set(root, (controlEpochs.get(root) ?? 0) + 1);
  pendingControls.add(root);
  clearTimeout(controlTimer);
  controlTimer = setTimeout(() => {
    const roots = [...pendingControls]; controlTimer = undefined;
    for (const node of roots) {
      if (activeControls.has(node)) continue;
      pendingControls.delete(node);
      if (!node.isConnected) { controlEpochs.delete(node); continue; }
      activeControls.add(node);
      void refreshBookControls(node).finally(() => {
        activeControls.delete(node);
        if (pendingControls.has(node)) queueBookControls(node);
      });
    }
  }, 100);
}
function mountBookControls(root: ParentNode): void {
  const mount = (node: HTMLElement) => { if (!controlEpochs.has(node)) queueBookControls(node); };
  if (root instanceof HTMLElement && root.matches('[data-ft-book-actions]')) mount(root);
  for (const node of root.querySelectorAll<HTMLElement>('[data-ft-book-actions]')) mount(node);
}
new MutationObserver(records => { for (const change of records) for (const node of change.addedNodes) if (node instanceof HTMLElement) mountBookControls(node); })
  .observe(document.documentElement, { childList: true, subtree: true });
mountBookControls(document);
for (const event of ['ft:activity-truth-committed', 'ft:book-unread', 'ft:caster-state-applied', 'ft:inventory-base-compiled']) window.addEventListener(event, raw => {
  const detail = record((raw as CustomEvent).detail), context = getSidelogContext();
  const actorId = upper(window.ftGetActiveCasterId?.() ?? context?.caster_id), encounterId = upper(context?.encounter_id);
  if (event === 'ft:caster-state-applied' && (upper(detail?.casterId) !== actorId || upper(detail?.encounterId) !== encounterId)) return;
  if (event === 'ft:inventory-base-compiled' && ![actorId, encounterId, ...bookOwners.values()].includes(upper(detail?.entityId))) return;
  if (event === 'ft:activity-truth-committed') {
    const command = record(detail?.command);
    if (!['book_read', 'book_unread'].includes(String(command?.kind)) || upper(command?.actor_id) !== actorId || upper(command?.encounter_id) !== encounterId) return;
  }
  for (const root of document.querySelectorAll<HTMLElement>('[data-ft-book-actions]')) {
    if (event !== 'ft:book-unread' || upper(root.dataset.ftBookActions) === upper(detail?.inventoryId)) queueBookControls(root);
  }
});
